import type { QuestionItem, QuestionTree } from 'pages/custom_tests/components/question_picker/QuestionPicker';
import { FormattedMessage, useIntl } from 'react-intl';

type TestSummaryBarProps = {
    minutes?: number;
    questionTree: QuestionTree;
    richSummary?: boolean;
    selectedQuestionIds: Set<number>;
    showActualTime?: boolean;
};

// Question types are identified by their resolved display name (numeric type id '1' as a
// fallback for Application) — never by substring-matching the numeric QuestionType field.
const isApplicationQuestion = (question: QuestionItem): boolean => question.QuestionTypeName === 'Application' || question.QuestionType === '1';

const isSimulationQuestion = (question: QuestionItem): boolean => question.QuestionTypeName === 'Simulation';

export const TestSummaryBar = (props: TestSummaryBarProps) => {

    const { minutes = 0, questionTree, richSummary = false, selectedQuestionIds, showActualTime = false } = props;

    const { formatMessage } = useIntl();

    const objectives = (questionTree.Products ?? [])
        .flatMap(product => product.Themes ?? [])
        .flatMap(theme => theme.Objectives ?? []);
    const questions = objectives.flatMap(objective => objective.Questions ?? []);

    const selectedTotal = selectedQuestionIds.size;
    const objectiveCount = objectives.filter(o => (o.Questions ?? []).some(q => selectedQuestionIds.has(q.QuestionId))).length;

    // Estimated time is 1 minute per selected question; once the user has configured (or is
    // editing) the real limit, show that actual value instead.
    const estMinutes = selectedTotal;
    const timeLabel = showActualTime ? (formatMessage({ id: 'time.446a3218', defaultMessage: 'Time' })) : (formatMessage({ id: 'est_time.9c3f3833', defaultMessage: 'Est. Time' }));
    const timeValue = showActualTime
        ? `${minutes} ${formatMessage({ id: 'mins.f79cb658', defaultMessage: 'mins' })}`
        : `~${estMinutes} ${formatMessage({ id: 'mins.f79cb658', defaultMessage: 'mins' })}`;

    // Unique selected questions, used for the per-type and per-source breakdowns.
    const selectedQuestionList = Array.from(
        new Map(questions.filter(q => selectedQuestionIds.has(q.QuestionId)).map(q => [q.QuestionId, q])).values()
    );
    const applicationCount = selectedQuestionList.filter(isApplicationQuestion).length;
    const simulationCount = selectedQuestionList.filter(isSimulationQuestion).length;
    const sourceCounts = selectedQuestionList.reduce((acc, q) => {

        const source = ((q as any).QuestionSource ?? q.Source ?? '').trim();
        if (source) acc[source] = (acc[source] ?? 0) + 1;
        return acc;

    }, {} as Record<string, number>);
    const sourceEntries = Object.entries(sourceCounts);

    const renderStat = (value: string | number, label: string) => (
        <div className={'border rounded bg-white px-3 py-2'}>
            <div className={'fw-bold'}>
                {value}
            </div>
            <div className={'small text-muted'}>
                {label}
            </div>
        </div>
    );

    return (
        <div className={'d-flex flex-wrap align-items-center justify-content-center gap-3 border rounded bg-light px-3 py-3'}>
            <div className={'d-flex flex-column me-2'}>
                <span className={'fw-bold'}>
                    <FormattedMessage defaultMessage="Test Summary" id="test_summary.acda5857" />
                </span>
                <span className={'small text-muted'}>
                    {`${selectedTotal} ${formatMessage({ id: 'questions_selected.11c36841', defaultMessage: 'questions selected' })}`}
                </span>
            </div>
            <div className={'d-flex flex-wrap gap-2'}>
                {renderStat(objectiveCount, formatMessage({ id: 'objectives.de5845c5', defaultMessage: 'Objectives' }))}
                {renderStat(timeValue, timeLabel)}
                {richSummary ?
                    (
                        <>
                            {renderStat(applicationCount, formatMessage({ id: 'application_questions.1e53b4c9', defaultMessage: 'Application Questions' }))}
                            {renderStat(simulationCount, formatMessage({ id: 'simulations.6fa41935', defaultMessage: 'Simulations' }))}
                            {renderStat(sourceEntries.length, formatMessage({ id: 'sources.9872e31e', defaultMessage: 'Sources' }))}
                            {sourceEntries.map(([source, count]) => (
                                <div className={'border rounded bg-white px-3 py-2'} key={source}>
                                    <div className={'fw-bold'}>
                                        {`${count} ${formatMessage({ id: 'questions.04b685ee', defaultMessage: 'questions' })}`}
                                    </div>
                                    <div className={'small text-muted'}>
                                        {`${formatMessage({ id: 'from.702cf68a', defaultMessage: 'from' })} ${source}`}
                                    </div>
                                </div>
                            ))}
                        </>
                    )
                    :
                    renderStat(applicationCount, formatMessage({ id: 'application_questions.1e53b4c9', defaultMessage: 'Application Questions' }))}
            </div>
        </div>
    );

};
